import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCw, Monitor, CheckCircle, ArrowRight, 
  FileSpreadsheet, Upload, Download, Shield
} from 'lucide-react';
import { Button } from '../ui/button';

const IntegrationShowcase = () => {
  const navigate = useNavigate();

  const compatibleSoftware = [
    { name: 'Vétocom', popular: true },
    { name: 'Bourgelat', popular: true },
    { name: 'Assistovet', popular: true },
    { name: 'DrVeto', popular: false },
    { name: "Vet'Phi", popular: false },
    { name: 'Vetup', popular: false },
    { name: 'GMVet', popular: false },
  ];

  const features = [
    {
      icon: Upload,
      title: "Import facile",
      description: "Transférez vos clients, animaux et historiques médicaux en quelques clics"
    },
    {
      icon: Download,
      title: "Export complet",
      description: "Récupérez vos données vetoRDV à tout moment au format CSV"
    },
    {
      icon: Shield,
      title: "Données sécurisées",
      description: "Chiffrement et respect du RGPD pour toutes vos données"
    }
  ];

  return (
    <section id="integration-section" className="py-20 bg-gradient-to-b from-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <RefreshCw className="w-4 h-4" />
            Nouveau : Intégration logiciels
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Connectez votre logiciel vétérinaire
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Synchronisez facilement vos données entre <strong>vetoRDV</strong> et votre logiciel 
            de gestion habituel. Aucune ressaisie nécessaire.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-8 text-white shadow-2xl">
              {/* Sync Animation Visual */}
              <div className="flex items-center justify-center gap-6 mb-8">
                <div className="bg-white/20 backdrop-blur rounded-2xl p-6 text-center">
                  <Monitor className="w-12 h-12 mx-auto mb-2" />
                  <span className="text-sm font-medium">Votre logiciel</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <RefreshCw className="w-8 h-8 animate-spin-slow" />
                  <div className="flex gap-1 mt-2">
                    <div className="w-2 h-2 bg-white/60 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-white/80 rounded-full animate-pulse delay-100"></div>
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse delay-200"></div>
                  </div>
                </div>
                
                <div className="bg-white/20 backdrop-blur rounded-2xl p-6 text-center">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2" />
                  <span className="text-sm font-medium">vetoRDV</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">100%</div>
                  <div className="text-xs text-indigo-200">Compatible</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">5 min</div>
                  <div className="text-xs text-indigo-200">Pour importer</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-bold">0€</div>
                  <div className="text-xs text-indigo-200">Gratuit</div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full shadow-lg font-medium text-sm">
              ✓ Import en 1 clic
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-8">
            {/* Compatible Software */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-indigo-600" />
                Compatible avec tous les logiciels
              </h3>
              <div className="flex flex-wrap gap-2">
                {compatibleSoftware.map((software, i) => (
                  <span 
                    key={i} 
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105 ${
                      software.popular 
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    {software.name}
                    {software.popular && <span className="ml-1">⭐</span>}
                  </span>
                ))}
                <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm border border-green-200 font-medium">
                  + Tout logiciel CSV
                </span>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <feature.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => navigate('/espace-veterinaire')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold"
              >
                Découvrir l'intégration
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold"
              >
                Besoin d'aide ?
              </Button>
            </div>

            {/* Trust indicator */}
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Accompagnement gratuit pour votre première synchronisation
            </p>
          </div>
        </div>

        {/* Bottom: How it works */}
        <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-8">
            Comment ça marche ?
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Exportez", desc: "Exportez vos données depuis votre logiciel au format CSV" },
              { step: 2, title: "Téléchargez", desc: "Téléchargez notre modèle pour voir le format attendu" },
              { step: 3, title: "Importez", desc: "Importez votre fichier CSV dans vetoRDV en 1 clic" },
              { step: 4, title: "C'est prêt !", desc: "Vos clients et animaux sont synchronisés" }
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3">
                  {item.step}
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
                
                {/* Arrow between steps */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-6 -right-3 text-gray-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </section>
  );
};

export default IntegrationShowcase;
